import {
  CLINICIAN_LABEL_CHOICES,
  CLINICIAN_LABEL_FREE_TEXT_MAX_LENGTH,
  CLINICIAN_LABEL_OTHER,
  CLINICIAN_REVIEW_DROPDOWN_Z_INDEX,
  CLINICIAN_SEVERITY_FIELD,
  clinicianLabelFieldKey,
  clinicianLabelFreeTextKey,
  clinicianLabelValueKey,
  clinicianSeverityKey,
  severityChoicesFor,
} from "@constants/clinicianLabelConstants";
import { ClinicianLabelField, ClinicianLabelValue, ClinicianSeverity } from "types/tests";
import { Select, TextInput } from "@mantine/core";

import { ClinicianLabelDraft } from "@utils/clinicianLabelUtils";
import { useTranslation } from "react-i18next";

interface ClinicianLabelFieldProps {
  field: ClinicianLabelField;
  draft: ClinicianLabelDraft;
  onChange: (field: ClinicianLabelField, draft: ClinicianLabelDraft) => void;
  disabled?: boolean;
  flex?: number | string;
}

export default function ClinicianLabelFields({
  field,
  draft,
  onChange,
  disabled,
  flex,
}: ClinicianLabelFieldProps) {
  const { t } = useTranslation();

  const severities = severityChoicesFor(draft.value);
  const hasSeverityScale = severities.length > 0;
  const showSeverity = field === CLINICIAN_SEVERITY_FIELD && Boolean(draft.value);
  const isOther = draft.value === CLINICIAN_LABEL_OTHER;

  const valueData = CLINICIAN_LABEL_CHOICES[field].map((value) => ({
    value,
    label: t(clinicianLabelValueKey(value)),
  }));

  const severityData = severities.map((severity) => ({
    value: severity,
    label: t(clinicianSeverityKey(severity)),
  }));

  function handleValueChange(next: string | null) {
    const value = next as ClinicianLabelValue | null;
    const keepSeverity =
      draft.severity && severityChoicesFor(value).includes(draft.severity) ? draft.severity : null;
    onChange(field, {
      value,
      severity: keepSeverity,
      freeText: value === CLINICIAN_LABEL_OTHER ? draft.freeText : "",
    });
  }

  return (
    <>
      <Select
        flex={flex}
        miw={160}
        label={t(clinicianLabelFieldKey(field))}
        placeholder={t("ClinicianLabel.notSelected")}
        data={valueData}
        value={draft.value}
        onChange={handleValueChange}
        disabled={disabled}
        clearable
        searchable
        nothingFoundMessage={t("ClinicianLabel.noMatch")}
        comboboxProps={{ zIndex: CLINICIAN_REVIEW_DROPDOWN_Z_INDEX }}
        data-testid={`clinician-label-select-${field}`}
      />
      {showSeverity && (
        <Select
          flex={flex}
          miw={160}
          label={t("ClinicianLabel.severity")}
          placeholder={
            hasSeverityScale
              ? t("ClinicianLabel.notSelected")
              : t("ClinicianLabel.severityNotApplicable")
          }
          withAsterisk={hasSeverityScale}
          data={severityData}
          value={draft.severity}
          onChange={(next) =>
            onChange(field, { ...draft, severity: next as ClinicianSeverity | null })
          }
          disabled={disabled || !hasSeverityScale}
          clearable={hasSeverityScale}
          comboboxProps={{ zIndex: CLINICIAN_REVIEW_DROPDOWN_Z_INDEX }}
          data-testid={`clinician-label-severity-${field}`}
        />
      )}
      {isOther && (
        <TextInput
          flex={flex}
          miw={160}
          label={t(clinicianLabelFreeTextKey(field))}
          withAsterisk
          value={draft.freeText}
          maxLength={CLINICIAN_LABEL_FREE_TEXT_MAX_LENGTH}
          onChange={(event) => onChange(field, { ...draft, freeText: event.currentTarget.value })}
          disabled={disabled}
          data-testid={`clinician-label-freetext-${field}`}
        />
      )}
    </>
  );
}
